import { FC, useMemo } from 'react';
import { useTheme } from 'styled-components';

import { ScPagination } from './styled';

export const MAX_VISIBLE_PAGES = 7;

interface PaginationProps {
  totalPages: number;
  currentPage: number;
  handlePageClick: (page: number) => void;
}

export const Pagination: FC<PaginationProps> = ({
  totalPages,
  currentPage,
  handlePageClick,
}) => {
  const { images } = useTheme();

  const numbers = useMemo(() => {
    return Array.from(Array(totalPages).keys()).map((i) => i + 1);
  }, [totalPages]);

  const paginationNumbers = useMemo(() => {
    if (numbers.length < 7) return numbers;

    if (currentPage === 1) {
      return [1, 2, '...', numbers.length];
    } else if ([2, 3].includes(currentPage)) {
      const sliced = numbers.slice(0, currentPage + 1);
      return [...sliced, '...', numbers.length];
    } else if (currentPage > 3 && currentPage < numbers.length - 2) {
      const sliced1 = numbers.slice(currentPage - 2, currentPage);
      const sliced2 = numbers.slice(currentPage, currentPage + 1);
      return [1, '...', ...sliced1, ...sliced2, '...', numbers.length];
    } else if (currentPage >= numbers.length - 2) {
      const sliced = numbers.slice(currentPage - 2);
      return [1, '...', ...sliced];
    }
    return [];
  }, [currentPage, numbers]);

  const handlePrevious = () => {
    if (currentPage > 1) handlePageClick(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) handlePageClick(currentPage + 1);
  };

  return (
    <ScPagination>
      <button
        onClick={handlePrevious}
        disabled={currentPage === 1}
        className="left-arrow"
      >
        <images.arrowLeftIcon.component />
      </button>
      {paginationNumbers.map((num, index) => (
        <button
          key={index}
          onClick={() => typeof num === 'number' && handlePageClick(num)}
          className={num === currentPage ? 'active' : ''}
        >
          {num}
        </button>
      ))}
      <button
        onClick={handleNext}
        disabled={currentPage === totalPages}
        className="right-arrow"
      >
        <images.arrowLeftIcon.component />
      </button>
    </ScPagination>
  );
};
